import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { presupuestosApi, empresasApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Eye, 
  Copy, 
  Trash2, 
  Send,
  FileText,
  AlertTriangle,
  TrendingUp
} from 'lucide-react';

export default function PresupuestosPage() {
  const { empresaActual, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [limites, setLimites] = useState(null);

  const loadPresupuestos = async () => {
    if (!empresaActual?.id) return;
    
    try {
      const estado = filtroEstado === 'todos' ? undefined : filtroEstado;
      const [presupuestosRes, limitesRes] = await Promise.all([
        presupuestosApi.getAll(empresaActual.id, estado),
        empresasApi.getLimites(empresaActual.id).catch(() => null)
      ]);
      setPresupuestos(presupuestosRes.data);
      if (limitesRes) setLimites(limitesRes.data);
    } catch (error) {
      console.error('Error loading presupuestos:', error);
      toast.error('Error al cargar los presupuestos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPresupuestos();
  }, [empresaActual?.id, filtroEstado]);

  const handleDuplicar = async (id) => {
    try {
      await presupuestosApi.duplicar(id);
      toast.success('Presupuesto duplicado');
      loadPresupuestos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este presupuesto?')) return;
    
    try {
      await presupuestosApi.delete(id);
      toast.success('Presupuesto eliminado');
      loadPresupuestos();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEnviar = async (id) => {
    try {
      await presupuestosApi.enviar(id);
      toast.success('Presupuesto enviado correctamente');
      loadPresupuestos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar');
    }
  };

  const filteredPresupuestos = presupuestos.filter(p => {
    const searchLower = searchTerm.toLowerCase();
    return (
      p.numero.toLowerCase().includes(searchLower) ||
      p.cliente?.nombre?.toLowerCase().includes(searchLower) ||
      p.cliente?.empresa_cliente?.toLowerCase().includes(searchLower)
    );
  });

  const canEdit = hasPermission(['OWNER', 'MANAGER']);
  
  // Calcular si está cerca del límite o ya lo alcanzó
  const presupuestosLimite = limites?.limites?.presupuestos;
  const cercaDelLimite = presupuestosLimite && presupuestosLimite.limite > 0 && 
    (presupuestosLimite.limite - presupuestosLimite.usado <= 2);
  const limiteAlcanzado = presupuestosLimite && presupuestosLimite.limite > 0 && 
    !presupuestosLimite.puede_crear;

  return (
    <DashboardLayout>
      <div className="space-y-4 lg:space-y-6 animate-fadeIn" data-testid="presupuestos-page">
        {/* Alerta de límite del plan */}
        {limiteAlcanzado && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              Has alcanzado el límite de <strong>{presupuestosLimite.limite} presupuestos</strong> del plan gratuito. 
              <Button variant="link" className="px-1 text-red-700 underline" onClick={() => navigate('/configuracion')}>
                Actualiza tu plan
              </Button> para enviar más presupuestos.
            </AlertDescription>
          </Alert>
        )}
        
        {cercaDelLimite && !limiteAlcanzado && (
          <Alert className="border-amber-200 bg-amber-50">
            <TrendingUp className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              Estás usando <strong>{presupuestosLimite.usado} de {presupuestosLimite.limite}</strong> presupuestos de tu plan. 
              <Button variant="link" className="px-1 text-amber-700 underline" onClick={() => navigate('/configuracion')}>
                Ver planes
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header - Desktop only */}
        <div className="hidden lg:flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Presupuestos
            </h1>
            <p className="text-slate-500 mt-1">
              Gestiona todos tus presupuestos y cotizaciones
              {presupuestosLimite && presupuestosLimite.limite > 0 && (
                <span className="ml-2 text-sm">
                  ({presupuestosLimite.usado}/{presupuestosLimite.limite} usados)
                </span>
              )}
            </p>
          </div>
          {canEdit && (
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate('/presupuestos/nuevo')}
              disabled={limiteAlcanzado}
              data-testid="presupuestos-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Presupuesto
            </Button>
          )}
        </div>

        {/* Mobile Header */}
        <div className="lg:hidden">
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Presupuestos
          </h1>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="presupuestos-search"
                />
              </div>
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="presupuestos-filter-estado">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="borrador">Borrador</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="aprobado">Aprobado</SelectItem>
                  <SelectItem value="rechazado">Rechazado</SelectItem>
                  <SelectItem value="vencido">Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : filteredPresupuestos.length === 0 ? (
          <Card>
            <div className="empty-state py-12 lg:py-16">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">No hay presupuestos</h3>
              <p className="empty-state-description text-sm">
                {searchTerm || filtroEstado !== 'todos' 
                  ? 'No se encontraron presupuestos'
                  : 'Crea tu primer presupuesto'}
              </p>
            </div>
          </Card>
        ) : (
          <>
            {/* Mobile List View */}
            <div className="lg:hidden space-y-3">
              {filteredPresupuestos.map((p) => (
                <Card 
                  key={p.id} 
                  className="cursor-pointer active:bg-slate-50"
                  onClick={() => navigate(`/presupuestos/${p.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-slate-900">{p.numero}</p>
                        <p className="text-sm text-slate-500">{p.cliente?.nombre || '-'}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(p.estado)}`}>
                        {getStatusLabel(p.estado)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <span className="text-sm text-slate-500">{formatDate(p.created_at)}</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(p.total)}</span>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                        {p.estado === 'borrador' && (
                          <Button 
                            size="sm" 
                            className="flex-1 h-9 bg-blue-600"
                            onClick={(e) => { e.stopPropagation(); handleEnviar(p.id); }}
                          >
                            <Send className="w-3 h-3 mr-1" /> Enviar
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-9"
                          onClick={(e) => { e.stopPropagation(); handleDuplicar(p.id); }}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-9 text-red-500"
                          onClick={(e) => { e.stopPropagation(); handleEliminar(p.id); }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop Table View */}
            <Card className="hidden lg:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="w-[70px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPresupuestos.map((p) => (
                    <TableRow 
                      key={p.id} 
                      className="table-row-hover cursor-pointer"
                      onClick={() => navigate(`/presupuestos/${p.id}`)}
                      data-testid={`presupuesto-row-${p.numero}`}
                    >
                      <TableCell className="font-medium">{p.numero}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{p.cliente?.nombre || '-'}</p>
                          <p className="text-sm text-slate-500">{p.cliente?.empresa_cliente || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>{formatDate(p.created_at)}</TableCell>
                      <TableCell className="font-medium">{formatCurrency(p.total)}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded border ${getStatusColor(p.estado)}`}>
                          {getStatusLabel(p.estado)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/presupuestos/${p.id}`); }}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver detalle
                            </DropdownMenuItem>
                            {canEdit && p.estado === 'borrador' && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEnviar(p.id); }}>
                                <Send className="w-4 h-4 mr-2" />
                                Enviar
                              </DropdownMenuItem>
                            )}
                            {canEdit && (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicar(p.id); }}>
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicar
                              </DropdownMenuItem>
                            )}
                            {canEdit && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={(e) => { e.stopPropagation(); handleEliminar(p.id); }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
